import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { JerseyColor } from '../../types/Player';

/**
 * Hook to manage the player check-in process
 */
export function useCheckInProcess() {
  const [jerseyNumber, setJerseyNumber] = useState('');
  const [jerseyColor, setJerseyColor] = useState<JerseyColor | null>(null);
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [message, setMessage] = useState('');
  const [checkInError, setCheckInError] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingCheckIn, setPendingCheckIn] = useState<{
    player: any;
    session: any;
    shirtNumber: number;
    shirtColor: JerseyColor;
  } | null>(null);

  // Initiate check-in
  const handleCheckIn = (player: any, session: any, jersey: string, color: JerseyColor) => {
    setPendingCheckIn({
      player,
      session,
      shirtNumber: parseInt(jersey, 10),
      shirtColor: color,
    });
    setConfirmDialogOpen(true);
  };

  // Cancel check-in
  const cancelCheckIn = () => {
    setConfirmDialogOpen(false);
    setPendingCheckIn(null);
  };

  // Confirm and process check-in
  const confirmCheckIn = async () => {
    if (!pendingCheckIn) return;
    try {
      setCheckInError('');
      setMessage('');
      setIsCheckingIn(true);

      // Validation
      if (!pendingCheckIn.player) {
        setCheckInError('No player selected for check-in.');
        setIsCheckingIn(false);
        return;
      }
      if (!pendingCheckIn.session) {
        setCheckInError('Please select an evaluation session.');
        setIsCheckingIn(false);
        return;
      }
      if (isNaN(pendingCheckIn.shirtNumber) || pendingCheckIn.shirtNumber <= 0) {
        setCheckInError('Please enter a valid jersey number (digits only).');
        setIsCheckingIn(false);
        return;
      }
      if (pendingCheckIn.shirtColor !== 'red' && pendingCheckIn.shirtColor !== 'blue') {
        setCheckInError('Please select the jersey color.');
        setIsCheckingIn(false);
        return;
      }

      const { error: rpcError } = await supabase.rpc('process_player_checkin', {
        player_id_param: pendingCheckIn.player.id,
        evaluation_session_id_param: pendingCheckIn.session.id,
        jersey_number_param: pendingCheckIn.shirtNumber,
        jersey_color_param: pendingCheckIn.shirtColor,
      });

      if (rpcError) {
        // Handle potential duplicate check-in error (or other errors)
        if (rpcError.code === '23505') { // unique_violation
          // Check if it's the specific constraint for player+session
          if (rpcError.message.includes('checkins_player_id_evaluation_session_id_key')) {
            setCheckInError('This player is already checked in for this session.');
          } else {
            setCheckInError(`Check-in failed: ${rpcError.message}`);
          }
        } else {
          setCheckInError(`Check-in failed: ${rpcError.message}`);
        }
        throw rpcError;
      }

      // Format time for success message
      const formattedTime = convertToAmPm(pendingCheckIn.session.start_time);
      setMessage(
        `${pendingCheckIn.player.first_name} ${pendingCheckIn.player.last_name} checked in successfully for session at ${formattedTime} with ${pendingCheckIn.shirtColor} jersey #${pendingCheckIn.shirtNumber}!`
      );

      // Reset form
      setJerseyNumber('');
      setJerseyColor(null);
      return true;
    } catch (err: any) {
      console.error('Error during check-in:', err);
      // Error already set in the try/catch block
      return false;
    } finally {
      setIsCheckingIn(false);
      setConfirmDialogOpen(false);
      setPendingCheckIn(null);
    }
  };

  // Utility function to display time in a friendly format
  function convertToAmPm(time24: string): string {
    if (!time24) return '';
    const [h, m] = time24.split(':');
    let hour = parseInt(h, 10);
    const period = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12;
    return `${hour}:${m} ${period}`;
  }

  return {
    jerseyNumber,
    setJerseyNumber,
    jerseyColor,
    setJerseyColor,
    isCheckingIn,
    message,
    setMessage,
    checkInError,
    setCheckInError,
    confirmDialogOpen,
    setConfirmDialogOpen,
    pendingCheckIn,
    handleCheckIn,
    confirmCheckIn,
    cancelCheckIn,
    convertToAmPm
  };
}
